import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Search, MapPin, Star, Loader2, ArrowRight, SearchX, WifiOff,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PublicLanguageToggle } from "../components/app/PublicLanguageToggle";
import { tr } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical service value → ES label. Mirrors SERVICE_TYPES in
// PublicLeadForm.jsx (kept local: that protected file doesn't export it).
const SERVICE_ES = {
  "Standard Clean": "Limpieza estándar",
  "Deep Clean": "Limpieza profunda",
  "Move-Out": "Mudanza (salida)",
  "Move-In": "Mudanza (entrada)",
  "Post-Construction": "Post-construcción",
  "Airbnb Turnover": "Rotación Airbnb",
  "Office / Commercial": "Oficina / Comercial",
};

const svcLabel = (value, lang) => (lang === "es" ? SERVICE_ES[value] || value : value);

const CleanerCard = ({ r, lang, t }) => (
  <div
    className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 flex flex-col gap-4"
    data-testid={`fc-result-card-${r.owner_id}`}
  >
    <div className="flex items-start gap-4">
      {r.booking_logo_url ? (
        <img
          src={r.booking_logo_url}
          alt={r.business_name}
          className="w-14 h-14 rounded-xl object-contain border border-gray-100 bg-white shrink-0"
          loading="lazy"
        />
      ) : (
        <span className="w-14 h-14 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center shrink-0" aria-hidden="true">
          <Sparkles className="w-6 h-6 text-green-800" />
        </span>
      )}
      <div className="min-w-0">
        <h3 className="font-display text-lg font-semibold text-gray-900 break-words" data-testid="fc-card-name">
          {r.business_name}
        </h3>
        <p className="mt-0.5 text-sm text-gray-500 flex items-center gap-1.5 flex-wrap">
          <MapPin className="w-3.5 h-3.5 text-green-700 shrink-0" aria-hidden="true" />
          <span data-testid="fc-card-distance">{t("fc.distance", r.distance_miles)}</span>
          {(r.booking_city || r.booking_state) && (
            <span className="text-gray-400">
              · {[r.booking_city, r.booking_state].filter(Boolean).join(", ")}
            </span>
          )}
        </p>
        {r.google_rating != null && (
          <p className="mt-1 text-sm text-gray-700 flex items-center gap-1" data-testid="fc-card-rating">
            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" aria-hidden="true" />
            <span className="font-semibold">{r.google_rating}</span>
            {r.google_review_count > 0 && (
              <span className="text-gray-500">{t("fc.reviews", r.google_review_count)}</span>
            )}
          </p>
        )}
      </div>
    </div>
    {Array.isArray(r.services) && r.services.length > 0 && (
      <div className="flex flex-wrap gap-1.5" data-testid="fc-card-services">
        {r.services.map((s) => (
          <span key={s} className="px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700">
            {svcLabel(s, lang)}
          </span>
        ))}
      </div>
    )}
    <Link
      to={`${r.booking_url_path}?src=find_a_cleaner&lang=${lang}`}
      className="mt-auto inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-green-800 hover:bg-green-900 text-white text-sm font-semibold transition-colors"
      data-testid={`fc-card-quote-btn-${r.owner_id}`}
    >
      {t("fc.getQuote")} <ArrowRight className="w-4 h-4" aria-hidden="true" />
    </Link>
  </div>
);

export default function FindACleaner() {
  const [lang, setLang] = useState(() => {
    try {
      return (navigator?.language || "").toLowerCase().startsWith("es") ? "es" : "en";
    } catch {
      return "en";
    }
  });
  const t = (key, ...args) => tr(key, lang, ...args);

  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // null = not searched yet; {known_zip, results} after a search
  const [data, setData] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{5}$/.test(zip)) {
      setError(t("fc.zipInvalid"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/find-cleaners`, { params: { zip } });
      setData(res.data);
    } catch (err) {
      setData(null);
      if (err?.response?.status === 429) setError(t("fc.tooMany"));
      else if (err?.response?.status === 400) setError(t("fc.zipInvalid"));
      else setError(t("fc.networkError"));
    } finally {
      setLoading(false);
    }
  };

  const showEmpty = data && (!data.known_zip || data.results.length === 0);

  return (
    <div className="min-h-screen bg-[#f9f9f8]" data-testid="find-cleaner-page">
      <div className="max-w-2xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="flex items-center justify-between mb-10 gap-3">
          <Link to="/" className="flex items-center gap-2" aria-label="Tasklio AI">
            <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
            </span>
            <span className="font-display font-bold text-lg">Tasklio AI</span>
          </Link>
          <PublicLanguageToggle lang={lang} setLang={setLang} testidPrefix="fc-lang-switch" />
        </div>

        <div className="max-w-2xl">
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-gray-900">
            {t("fc.title")}
          </h1>
          <p className="mt-3 text-gray-600 text-base leading-relaxed">{t("fc.subtitle")}</p>
        </div>

        <form onSubmit={search} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl" noValidate>
          <div className="flex-1">
            <label htmlFor="fc-zip" className="sr-only">{t("fc.zipLabel")}</label>
            <Input
              id="fc-zip"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
              placeholder={t("fc.zipPlaceholder")}
              inputMode="numeric"
              maxLength={5}
              autoComplete="postal-code"
              aria-describedby={error ? "fc-error" : undefined}
              aria-invalid={error ? "true" : undefined}
              className="h-12 text-base bg-white"
              data-testid="fc-zip-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="h-12 px-7 bg-green-800 hover:bg-green-900 text-white font-semibold"
            data-testid="fc-search-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t("fc.searching")}
              </>
            ) : (
              <>
                <Search className="w-4 h-4" aria-hidden="true" /> {t("fc.searchBtn")}
              </>
            )}
          </Button>
        </form>
        {error && (
          <p id="fc-error" role="alert" className="mt-3 text-sm font-medium text-red-600 flex items-center gap-1.5" data-testid="fc-error">
            <WifiOff className={`w-4 h-4 ${error === t("fc.networkError") ? "" : "hidden"}`} aria-hidden="true" />
            {error}
          </p>
        )}

        <div className="mt-10">
          {showEmpty && (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-2xl" data-testid="fc-empty-state">
              <span className="w-14 h-14 rounded-full bg-gray-100 mx-auto flex items-center justify-center" aria-hidden="true">
                <SearchX className="w-7 h-7 text-gray-400" />
              </span>
              <h2 className="font-display mt-5 text-xl font-semibold text-gray-900">{t("fc.emptyTitle")}</h2>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{t("fc.emptyBody")}</p>
            </div>
          )}
          {data && data.results.length > 0 && (
            <>
              <p className="text-sm text-gray-500 mb-4" data-testid="fc-results-count">
                {t("fc.resultsFor", data.zip)}
              </p>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="fc-results-grid">
                {data.results.map((r) => (
                  <CleanerCard key={r.owner_id} r={r} lang={lang} t={t} />
                ))}
              </div>
            </>
          )}
        </div>

        <p className="mt-14 text-xs text-gray-400">{t("fc.distanceNote")}</p>
      </div>
    </div>
  );
}
